import { useEffect, useRef, useState } from 'react';
import Uppy from '@uppy/core';
import { useDispatch, useReduxState } from 'hooks';
import { Alert, Button } from 'components/common';
import { Icon } from 'components/common/icons';
import { Checkbox, Form, TextArea } from 'components/common/form';
import { FileUploader } from 'components/common/uploader';
import { ReaderEmojiPicker } from 'components/reader/common';
import { ReaderBlockDiscussionCommentAttachment } from '.';

import type { EmojiData } from 'emoji-mart';
import type { FormApi } from 'informed';
import type { ButtonSize } from 'components/common/Button';
import type { CommonBlockPropsReader } from 'components/reader/blocks';
import type {
  DiscussionSubmitObj,
  DiscussionComment
} from './ReaderBlockDiscussion';

interface FormValues {
  isAnonymous: boolean;
  text: string;
}

interface Props extends Pick<CommonBlockPropsReader<'discussion'>, 'fontSize'> {
  allowAnonymousComments?: boolean;
  comment?: DiscussionComment;
  isChildForm: boolean;
  isEditMode?: boolean;
  onCancel: () => void;
  onSubmit: (saveObj: DiscussionSubmitObj) => Promise<void>;
  parentCommentId: number;
}

const ReaderBlockDiscussionCommentForm = ({
  allowAnonymousComments,
  comment,
  isChildForm,
  isEditMode,
  fontSize,
  onCancel,
  onSubmit,
  parentCommentId
}: Props) => {
  const { dispatch } = useDispatch();

  // for the uploaders, the correct type seems to be Uppy.Uppy
  // however, we have to figured out how to type the upload response...
  const fileUploader = useRef<any>(undefined);
  const formApi = useRef<FormApi<FormValues>>(undefined);
  const imageUploader = useRef<any>(undefined);
  const textAreaContainer = useRef<HTMLDivElement>(undefined);

  const {
    messages,
    pageState: { offline, readOnly },
    theme
  } = useReduxState();

  const [attachment, setAttachment] = useState(
    isEditMode && comment ? comment.attachment : null
  );
  const [attachmentType, setAttachmentType] = useState(
    isEditMode && comment ? comment.attachmentType : null
  );
  const [currentPosition, setCurrentPosition] = useState(0);
  const [fileAdded, setFileAdded] = useState(false);
  const [imageAdded, setImageAdded] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    textAreaContainer.current.querySelector('textarea').focus();

    return () => clearTimeout(currentPositionTimeout);
  }, []);

  const handleAttachmentRemove = () => {
    setAttachment(null);
    setAttachmentType(null);
  };

  const handleEmojiSelect = (emoji: EmojiData) => {
    let currentText: string = formApi.current.getValue('text') || '';
    if (currentPosition < currentText.length)
      currentText = `${currentText.slice(0, currentPosition)}${
        emoji.colons
      }${currentText.slice(currentPosition)}`;
    else currentText += emoji.colons;

    formApi.current.setValue('text', currentText);
  };

  const handleSubmit = async ({ isAnonymous, text }: FormValues) => {
    try {
      setLoading(true);

      // upload file or image before submitting form
      const fileInComment = { attachment, attachmentType };

      let uploadError = fileAdded || imageAdded;
      if (fileAdded || imageAdded) {
        const { type, uploader } = fileAdded
          ? { type: 'file' as const, uploader: fileUploader.current }
          : { type: 'image' as const, uploader: imageUploader.current };

        const result = await uploader.upload();
        if (result) {
          fileInComment.attachment = result.url;
          fileInComment.attachmentType = type;

          setAttachment(result.url);
          setAttachmentType(type);
          uploadError = false;
        }
      }

      if (!uploadError) {
        const submitData = {
          attachment: fileInComment.attachment,
          attachmentType: fileInComment.attachmentType,
          isAnonymous,
          text
        };

        if (isEditMode && comment) {
          await onSubmit({ ...submitData, commentId: comment.id });
        } else await onSubmit({ ...submitData, parentCommentId });

        onCancel();
      }
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setLoading(false);
    }
  };

  let currentPositionTimeout = null;
  const handleUpdateCurrentPosition = (
    ev: React.KeyboardEvent | React.MouseEvent
  ) => {
    if (ev) {
      clearTimeout(currentPositionTimeout);

      const target = ev.target as HTMLTextAreaElement;
      currentPositionTimeout = setTimeout(
        () => setCurrentPosition(target.selectionStart),
        1000
      );
    }
  };

  const setFileUploader = (uploader: Uppy.Uppy) =>
    (fileUploader.current = uploader);

  const setImageUploader = (uploader: Uppy.Uppy) =>
    (imageUploader.current = uploader);

  const validateText = (value: string) => {
    return !value || !value.toString().trim()
      ? messages.commonFieldRequired
      : undefined;
  };

  return readOnly ? null : (
    <div className="comment-form">
      <Form getApi={(form) => (formApi.current = form)} onSubmit={handleSubmit}>
        {({ formState }) => (
          <div className="form">
            <div className="input">
              <TextArea
                aria-label={messages.readerDiscussionWriteComment}
                autoGrow={true}
                error={formState.errors.text}
                field="text"
                initialValue={isEditMode && comment ? comment.text : ''}
                onClick={handleUpdateCurrentPosition}
                onKeyDown={handleUpdateCurrentPosition}
                placeholder={messages.readerDiscussionWriteComment}
                size={fontSize}
                textareaContainerRef={(el: HTMLDivElement) =>
                  (textAreaContainer.current = el)
                }
                validate={validateText}
                validateOnChange={true}
              />

              <div className="picker">
                <ReaderEmojiPicker
                  buttonColor={theme.textColorLighter}
                  buttonColorActive={theme.textColor}
                  disabled={offline}
                  onSelect={handleEmojiSelect}
                />
              </div>
            </div>

            <Alert
              size={
                {
                  small: 'small',
                  medium: 'small',
                  large: 'medium',
                  huge: 'medium'
                }[fontSize] as 'small' | 'medium'
              }
              style={{ marginBottom: 10 }}
              text={messages.readerDiscussionWriteCommentInfo}
              type={isChildForm ? 'info-dark' : 'info'}
            />

            <div className="attachments">
              {isEditMode && attachment && !imageAdded ? (
                <div className="existing-attachment">
                  <ReaderBlockDiscussionCommentAttachment
                    attachment={attachment}
                    attachmentType={attachmentType}
                    disabled={loading || readOnly}
                    fontSize={fontSize}
                    onRemove={handleAttachmentRemove}
                  />
                </div>
              ) : (
                <>
                  <div
                    className="file-uploader"
                    style={{ display: imageAdded ? 'none' : 'inline-block' }}
                  >
                    <FileUploader
                      buttonDarkStyle={isChildForm}
                      getUploader={setFileUploader}
                      id={`discussion-file-uploader-${
                        parentCommentId || 'root'
                      }`}
                      keyPrefix="uploads/files/"
                      onFileAdded={() => setFileAdded(true)}
                      onFileRemoved={() => setFileAdded(false)}
                      placeholder={
                        <span>
                          <Icon
                            icon={['far', 'file']}
                            style={{ marginRight: 7 }}
                          />
                          {messages.readerDiscussionAttachFile}
                        </span>
                      }
                      uuidAsFilename={false}
                    />
                  </div>
                  <div
                    className="image-uploader"
                    style={{ display: fileAdded ? 'none' : 'inline-block' }}
                  >
                    <FileUploader
                      allowedFileTypes={['image/*']}
                      buttonDarkStyle={isChildForm}
                      getUploader={setImageUploader}
                      id={`discussion-image-uploader-${
                        parentCommentId || 'root'
                      }`}
                      keyPrefix="uploads/images/original/"
                      onFileAdded={() => setImageAdded(true)}
                      onFileRemoved={() => setImageAdded(false)}
                      placeholder={
                        <span>
                          <Icon
                            icon={['far', 'file-image']}
                            style={{ marginRight: 7 }}
                          />
                          {messages.readerDiscussionEmbedImage}
                        </span>
                      }
                      uuidAsFilename={true}
                    />
                  </div>
                </>
              )}
            </div>

            {!isEditMode && allowAnonymousComments ? (
              <div className="anonymous">
                <Checkbox
                  field="isAnonymous"
                  id={`discussion-is-anonymous-${parentCommentId || '0'}`}
                  label={messages.readerCommonSendAsAnonymous}
                />
              </div>
            ) : null}

            <div className="controls">
              <Button
                category="light"
                onClick={() => onCancel()}
                size={
                  {
                    small: 'medium',
                    medium: 'medium',
                    large: 'large',
                    huge: 'large'
                  }[fontSize] as ButtonSize
                }
                text={messages.commonCancel}
              />
              <Button
                category="success"
                disabled={offline}
                {...(offline
                  ? {
                      'aria-label': messages.readerDiscussionOffline,
                      'data-tooltip': messages.readerDiscussionOffline
                    }
                  : {})}
                loading={loading}
                size={
                  {
                    small: 'medium',
                    medium: 'medium',
                    large: 'large',
                    huge: 'large'
                  }[fontSize] as ButtonSize
                }
                style={{ marginLeft: 10 }}
                text={
                  isEditMode
                    ? messages.commonSaveChanges
                    : messages.commonSubmit
                }
                type="submit"
              />
            </div>
          </div>
        )}
      </Form>

      <style jsx>{`
        .form {
          background-color: ${isChildForm ? theme.lightGrey : 'white'};
          border-radius: ${theme.cardBorderRadius};
          padding: ${isEditMode ? 0 : '15px'};
        }
        .input {
          position: relative;
        }
        .picker {
          position: absolute;
          bottom: 5px;
          right: 0;
        }
        .attachments {
          margin: 5px 0 10px;
        }
        .anonymous {
          margin: 15px 0 10px;
        }
        .file-uploader {
          margin-right: 10px;
        }
        .controls {
          margin-top: 5px;
          text-align: right;
        }
      `}</style>
    </div>
  );
};

export default ReaderBlockDiscussionCommentForm;
